import * as React from 'react';
import {
  MenuList,
  MenuItem,
  MenuGroup,
  makeStyles,
  shorthands,
} from '@fluentui/react-components';
import { 
  Rocket24Regular, 
  CalendarLtr24Regular, 
  HeartPulse24Regular,  
  People24Regular, 
  PlugConnectedSettings24Regular,
  Toolbox24Regular,
  BookDatabase24Regular,
  TagMultiple24Regular,
  Folder24Regular,
  Board24Regular,
} from '@fluentui/react-icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLayout } from '../../contexts/LayoutContext';
import { useCurrentUser } from '../../contexts/UserContext';

// Interface para definir a estrutura dos itens do menu
interface MenuItemData {
  icon: React.ComponentType;
  label: string;
  route?: string; // Rota opcional para navegação
  allowedUserTypes?: number[]; // Tipos de usuário que podem ver este item (ex: [0] para Usuario, [0,1] para ambos)
}

// Interface para grupos de menu
interface MenuGroupData {
  label?: string; // Opcional, pois alguns grupos podem não ter label
  items: MenuItemData[];
}

// Dados fixos do menu organizados por grupos
// Para restringir um item do menu a tipos específicos de usuário,
// adicione a propriedade allowedUserTypes com array de tipos permitidos:
// - [0]: Apenas Usuário/Pessoa física
// - [1]: Apenas Empresa
// - [2]: Administrador
// - [0, 1]: Ambos os tipos
// - undefined/omisso: Todos os tipos (padrão)
const menuData: MenuGroupData[] = [
  {
    label: 'Dashboards',
    items: [
      { icon: Board24Regular, label: 'Visão geral', route: '/dashboard' },
    ],
  },
  {
    label: 'Fast Track',
    items: [
      { icon: HeartPulse24Regular, label: 'Diagnóstico', route: '/diagnostico' },
      { icon: Rocket24Regular, label: 'Planos de ação' },
      { icon: CalendarLtr24Regular, label: 'Acompanhamento' },
    ],
  }, 
  {
    label: 'Projetos',
    items: [
      { icon: Folder24Regular, label: 'Projetos', route: '/projects' },
      { icon: BookDatabase24Regular, label: 'Eventos', route: '/events', allowedUserTypes: [1,2] },
      { icon: TagMultiple24Regular, label: 'Marcadores', route: '/markers', allowedUserTypes: [1,2] },
    ],
  },
  {
    label: 'Configurações',
    items: [
      { icon: People24Regular, label: 'Usuários', route: '/users', allowedUserTypes: [1,2] },
      { icon: PlugConnectedSettings24Regular, label: 'Integrações', route: '/integrations', allowedUserTypes: [1,2] },      
    ],
  }
];

const useStyles = makeStyles({
  sidebar: {
    position: 'fixed',
    top: 0,
    left: 0,
    height: '100vh',
    backgroundColor: '#343a40',
    borderRight: '1px solid #3a3a3a',
    transition: 'width 0.3s ease, transform 0.3s ease',
    zIndex: 1000,
    display: 'flex',
    flexDirection: 'column',
    '@media (max-width: 768px)': {
      position: 'fixed',
      zIndex: 999,
      transform: 'translateX(-100%)',
    },
  },
  sidebarOpen: {
    '@media (max-width: 768px)': {
      transform: 'translateX(0)',
    },
  },
  collapsed: {
    width: '60px',
  },
  expanded: {
    width: '240px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    height: '56px',
    borderBottom: '1px solid #ffffff33',
    position: 'relative',
  },
  menu: {
    flex: 1,
    padding: '16px 8px',
  },
  groupSeparator: {
    height: '1px',
    backgroundColor: '#ffffff33',
    margin: '12px 16px',
    border: 'none',
  },
  groupHeader: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#ffffff99',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    padding: '8px 16px 4px 16px',
    marginBottom: '4px',
  },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    margin: '2px 4px',
    ...shorthands.borderRadius('8px'),
    color: '#ffffff',
    backgroundColor: 'transparent',
    transition: 'all 0.2s ease',
    ':hover': {
      backgroundColor: '#4b545c',
      color: '#ffffff',
      transform: 'translateX(2px)',
    },
    ':hover svg': {
      color: '#ffffff',
      fill: '#ffffff',
    },
    '& svg': {
      fontSize: '20px',
      width: '20px',
      height: '20px',
      color: '#ffffff',
      fill: '#ffffff',
    },
  },
  menuItemActive: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    margin: '2px 4px',
    ...shorthands.borderRadius('8px'),
    color: '#ffffff',
    backgroundColor: '#3f474e',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
    transition: 'all 0.2s ease',
    ':hover': {
      backgroundColor: '#4b545c',
      color: '#ffffff',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
      transform: 'translateX(2px)',
    },
    ':hover svg': {
      color: '#ffffff',
      fill: '#ffffff',
    },
    '& svg': {
      fontSize: '20px',
      width: '20px',
      height: '20px',
      color: '#ffffff',
      fill: '#ffffff',
    },
  },
  collapsedItem: {
    justifyContent: 'center',
    padding: '12px 8px',
    margin: '2px 4px',
    ...shorthands.borderRadius('8px'),
    color: '#ffffff',
    backgroundColor: 'transparent',
    transition: 'all 0.2s ease',
    ':hover': {
      backgroundColor: '#3a3a3a',
      color: '#ffffff',
      transform: 'translateX(2px)',
    },
    ':hover svg': {
      color: '#ffffff',
      fill: '#ffffff',
    },
    '& svg': {
      fontSize: '20px',
      width: '20px',
      height: '20px',
      color: '#ffffff',
      fill: '#ffffff',
    },
  },
  collapsedItemActive: {
    justifyContent: 'center',
    padding: '12px 8px',
    margin: '2px 4px',
    ...shorthands.borderRadius('8px'),
    color: '#ffffff',
    backgroundColor: '#3f474e',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
    transition: 'all 0.2s ease',
    ':hover': {
      backgroundColor: '#4b545c',
      color: '#ffffff',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
      transform: 'translateX(2px)',
    },
    ':hover svg': {
      color: '#ffffff',
      fill: '#ffffff',
    },
    '& svg': {
      fontSize: '20px',
      width: '20px',
      height: '20px',
      color: '#ffffff',
      fill: '#ffffff',
    },
  },
  logo: {
    flex: 1,
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#ffffff',
    textAlign: 'center',
  },
});

export default function Sidebar() {
  const styles = useStyles();
  const { sidebarCollapsed, sidebarOpen, setSidebarOpen } = useLayout();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useCurrentUser();

  // Filtrar itens do menu baseado no tipo do usuário
  const getFilteredMenuData = (): MenuGroupData[] => {
    return menuData.map(group => ({
      ...group,
      items: group.items.filter(item => {
        // Se não há restrição de tipos, mostrar para todos
        if (!item.allowedUserTypes) {
          return true;
        }
        // Verificar se o tipo do usuário atual está na lista permitida
        return currentUser?.tipo !== undefined && item.allowedUserTypes.includes(currentUser.tipo);
      })
    })).filter(group => group.items.length > 0); // Remover grupos vazios
  };

  const filteredMenuData = getFilteredMenuData();

  const handleMenuItemClick = (item: MenuItemData) => {
    if (item.route) {
      navigate(item.route);
      // Fechar o sidebar em dispositivos móveis após navegação
      if (window.innerWidth < 768) {
        setSidebarOpen(false);
      }
    }
  };

  const isItemActive = (item: MenuItemData) => {
    return item.route && location.pathname === item.route;
  };

  return (
    <div className={`${styles.sidebar} ${sidebarOpen ? styles.sidebarOpen : ''} ${sidebarCollapsed ? styles.collapsed : styles.expanded}`}>
      
      <div className={styles.header}>
        <div className={styles.logo}>
          {sidebarCollapsed ? <Toolbox24Regular /> : 'IMPLANTEI ERP'}
        </div>
      </div>
      
      <MenuList className={styles.menu}>
        {filteredMenuData.map((group, groupIndex) => (
          <React.Fragment key={groupIndex}>
            {group.label && !sidebarCollapsed && (
              <div className={styles.groupHeader}>
                {group.label}
              </div>
            )}
            <MenuGroup>
              {group.items.map((item, itemIndex) => {
                const isActive = isItemActive(item);
                return (
                  <MenuItem 
                    key={itemIndex} 
                    icon={<item.icon />} 
                    className={
                      sidebarCollapsed 
                        ? (isActive ? styles.collapsedItemActive : styles.collapsedItem)
                        : (isActive ? styles.menuItemActive : styles.menuItem)
                    }
                    onClick={() => handleMenuItemClick(item)}
                  >
                    {!sidebarCollapsed && <span>{item.label}</span>}
                  </MenuItem>
                );
              })}
            </MenuGroup>
            {/* Adicionar separador entre grupos (exceto o último) */}
            {groupIndex < filteredMenuData.length - 1 && (
              <hr className={styles.groupSeparator} />
            )}
          </React.Fragment>
        ))}
      </MenuList>
    </div>
  );
}
